import React from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import useData from '@/utils/useData';
import StoryboardViewContainer from '@/views/storyboard/StoryboardViewContainer';
import CellLoader from '@/components/loading/CellLoader';  
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import i18n from 'i18n';
import {buildURL} from '@/utils/urlBuilder';
import {hasPermission} from '@/utils/permissions/permissionDebug';

interface TeamData {
  id: number;
  name: string;
  external_id: string;
}

interface Story {
  id: number;
  assignedUrl: string;
  firstStoryboardHashid: string;
  firstStoryboardSlug: string;
}

const TeamDetailView: React.FC = () => {
    const {teamId} = useParams<{ teamId: string }>();
    const [searchParams] = useSearchParams();
    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const teamDetailUrl = teamId ? buildURL('teamDetails', {team_id: teamId}) : '';
    const [, loadingTeamData] = useData<TeamData>(teamDetailUrl, {useLocalStore: true});

    const [personsStories, loadingPersonsStories] = useData<Story[]>(window.URLS.storiesResource, {
        storeResolver: 'personsStories',
        initialValue: [],
        requestParams: {
            params: {storyListType: 'personsStories'},
        },
    });

    if (loadingTeamData || loadingUserData || loadingPersonsStories || !teamId) {
        return <CellLoader />;
    }

    const userCanAccessSpecialStoryViewObject = hasPermission('fs.story.list', userData?.permissions, true);

    if (!userCanAccessSpecialStoryViewObject) {
        return (
            <div className="alert alert-danger" role="alert">
                {i18n.t('tt_you_dont_have_permission_to_access_the_requested_resource.text') || 'You don\'t have permission to view this page'}
            </div>
        );
    }

    // Find the teams story from personsStories
    const teamsStory = personsStories.find((story: Story) => story.assignedUrl === 'teams');
    
    if (!teamsStory) {
        return (
            <div className="alert alert-warning" role="alert">
                No analytics dashboard configured for teams. Please contact your administrator.
            </div>
        );
    }

    const storyboardParam = searchParams.get('storyboard');
    const slugParam = searchParams.get('slug');

    const activeStoryboardHashid = storyboardParam || teamsStory.firstStoryboardHashid;
    const activeStoryboardUrl = slugParam || teamsStory.firstStoryboardSlug;

    return (
        <StoryboardViewContainer
            key={`${activeStoryboardHashid}-${activeStoryboardUrl}-${teamId}`}
            activeStoryboardHashid={activeStoryboardHashid}
            activeStoryUrl="teams"
            activeStoryboardUrl={activeStoryboardUrl}
            activeStoryObject={teamId}
            hideNavigation={false}
        />
    );
};

export default TeamDetailView;

